import React from 'react';
import { getClass } from '../../../utils/component-helpers';
import { getStatCards } from '../../admin/bid-center/utils';
import { formatValue, formatCurrencyThousands } from '../common/utils';

import arrowRedDownIcon from '../../../images/icons/icon-arrow-red-down.svg';
import arrowOrangeUpIcon from '../../../images/icons/icon-arrow-orange-up.svg';
import checkMarkIcon from '../../../images/icons/icon-checkmark.svg';
import './stat-cards.scss';

const iconsMapped = {
  green: checkMarkIcon,
  yellow: arrowOrangeUpIcon,
  red: arrowRedDownIcon,
};

const StatCards = ({ data, statType, strategyId, hidePercentDifference, hideTargetPacing }) => {
  const isCampaignCenter = [
    'campaign-center-campaigns-tab',
    'campaign-center-strategies-tab',
  ].includes(statType);

  const isMerchantPerformanceOverview = statType === 'merchant-performance-overview';

  const format = (card, cardStats, cardStatType) => {
    if (isMerchantPerformanceOverview || isCampaignCenter) {
      if (card.name === 'roas') {
        if (['goal', 'projected'].includes(cardStatType) && !strategyId) return '-';
        if (cardStatType === 'current') return parseFloat(cardStats[cardStatType]).toFixed(1);
      }
      if (card.name === 'revenue' && cardStatType === 'goal' && !strategyId) return '-';
      if (cardStatType === 'current' && parseInt(cardStats[cardStatType]) === 0) {
        if (['spend', 'revenue'].includes(card.name)) return '$0';
        if (card.name === 'roas') return '0.0';
      }
    }
    const cardFullName = `${`${card.name}_${cardStatType}`}`;
    const cardType = cardStatType === 'percent_difference' ? 'percent' : card.type;

    const val = ['revenue', 'spend'].includes(card.name)
      ? parseFloat(cardStats[cardStatType]).toFixed(1)
      : cardStats[cardStatType];

    const formattedValue = ['percent_difference', 'percent_from_target'].includes(cardStatType)
      ? formatValue(val, cardType, cardFullName, false)
      : formatValue(val, cardType, cardFullName, true);

    return cardType === 'currency' && formattedValue !== '-' && !isCampaignCenter
      ? formatCurrencyThousands(formattedValue, cardStatType === 'current' ? 'K' : null)
      : formattedValue;
  };

  return (
    <div className="stat-cards">
      {getStatCards(statType).map((card, index) => {
        // TODO: Set data to goals if business decides we don't need the other values in totals
        const cardStats = data.goals.filter(goal => goal.name === card.name)[0];
        const positivePercent = parseInt(cardStats.percent_difference) > 0;
        const pacingDiff = parseInt(cardStats.projected) - parseInt(cardStats.goal);
        const pacingPercent = (pacingDiff / parseInt(cardStats.goal)) * 100;
        let paceIndicator =
          parseInt(cardStats.projected) >= parseInt(cardStats.goal) && pacingPercent <= 5
            ? 'green'
            : pacingPercent > 5
            ? 'yellow'
            : 'red';
        const percentDifference = format(card, cardStats, 'percent_difference');
        let projected = format(card, cardStats, 'projected');
        if (card.name === 'roas' && strategyId) {
          const fromTarget = parseInt(cardStats.percent_from_target);
          paceIndicator = fromTarget >= 5 ? 'yellow' : fromTarget <= -5 ? 'red' : 'green';
          projected = Math.round(format(card, cardStats, 'percent_from_target'));
        }
        const goal = format(card, cardStats, 'goal');

        const paceImg = <img src={iconsMapped[paceIndicator]} alt={paceIndicator} />;
        const paceIcon = goal !== '-' && !isMerchantPerformanceOverview ? paceImg : null;
        const paceIcon2 =
          goal !== '-' && projected !== '-' && isMerchantPerformanceOverview ? paceImg : null;

        const targetText =
          card.name === 'spend' && isMerchantPerformanceOverview && !strategyId
            ? 'Merchant Monthly Target'
            : 'Target';

        const pacing =
          card.name === 'roas' ? (
            <div className="pacing">
              <span className="value">
                {projected !== null && projected !== undefined ? projected : '-'}
              </span>
              % from Target&nbsp;
            </div>
          ) : (
            <div className="pacing">
              Pacing towards&nbsp;
              <span className="value">{projected}</span>
            </div>
          );

        const percentDifferenceEl = !hidePercentDifference ? (
          <div className="percent-difference">
            <span
              className={getClass('value', {
                green: positivePercent,
                red: !positivePercent,
              })}
            >
              {`
								${positivePercent ? '+' : ''}
								${percentDifference}
							`}
            </span>
            <span className="from-last-month">
              {`(from ${strategyId ? 'yesterday' : 'last month'})`}
            </span>
          </div>
        ) : null;

        const targetPacing = !hideTargetPacing ? (
          <div className="target-pacing">
            {pacing}
            {paceIcon2}
            <div className="line">|</div>
            <div className="target">
              {targetText}:&nbsp;
              <span className="value">{goal}</span>
              {paceIcon}
            </div>
          </div>
        ) : null;

        return (
          <div key={index} className={`card ${cardStats.name}`}>
            <div className="label-current-percent">
              <p>{card.label}</p>
              <p className="value">{format(card, cardStats, 'current')}</p>
              {percentDifferenceEl}
            </div>
            {targetPacing}
          </div>
        );
      })}
    </div>
  );
};

export default StatCards;
